import { Component } from '@theme/component';

/**
 * @typedef {Object} TextTruncateRefs
 * @property {HTMLElement} textContainer - The text container element
 * @property {HTMLElement} toggleButton - The button to toggle truncation
 */

/**
 * A custom element that manages text truncation with "Mehr anzeigen" functionality
 * Uses line-clamp for initial truncation and smooth height animation for expand/collapse
 *
 * @extends {Component<TextTruncateRefs>}
 */
class TextTruncateComponent extends Component {
  requiredRefs = ['textContainer', 'toggleButton'];

  /**
   * @type {boolean}
   */
  #expanded = false;

  /**
   * @type {number}
   */
  #collapsedHeight = 0;

  /**
   * @type {Animation | undefined}
   */
  #animation;

  /**
   * @constant {number}
   */
  #animationSpeed = 300;

  /**
   * @constant {string}
   */
  #animationEasing = 'cubic-bezier(0.33, 1, 0.68, 1)'; // ease-out-cubic

  /**
   * @type {boolean}
   */
  #isMuiVariant = false;

  connectedCallback() {
    super.connectedCallback();
    
    // Wait for refs to be initialized
    requestAnimationFrame(() => {
      // Check if we're inside an expandable-item (MUI accordion)
      this.#isMuiVariant = this.dataset.muiVariant === 'true' || 
                          this.closest('.mui-latin-1oysw9n') !== null ||
                          this.closest('.expandable') !== null;
      
      // Add MUI variant class if detected
      if (this.#isMuiVariant && !this.classList.contains('text-truncate--mui')) {
        this.classList.add('text-truncate--mui');
        this.dataset.muiVariant = 'true';
      } else if (!this.#isMuiVariant) {
        // If not MUI variant, use standard structure
        this.#applyStandardStructure();
      }
      
      // Add click event listener to button
      if (this.refs.toggleButton) {
        if (!this.refs.toggleButton.hasAttribute('data-listener-attached')) {
          this.refs.toggleButton.addEventListener('click', this.toggle);
          this.refs.toggleButton.setAttribute('data-listener-attached', 'true');
        }
      } else {
        console.warn('text-truncate: toggleButton ref not found', this);
      }
      
      this.#initTruncation();
    });
  }

  updatedCallback() {
    super.updatedCallback();
    // Re-initialize after update
    requestAnimationFrame(() => {
      if (this.refs.toggleButton && !this.refs.toggleButton.hasAttribute('data-listener-attached')) {
        this.refs.toggleButton.addEventListener('click', this.toggle);
        this.refs.toggleButton.setAttribute('data-listener-attached', 'true');
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Remove event listener
    if (this.refs.toggleButton) {
      this.refs.toggleButton.removeEventListener('click', this.toggle);
      this.refs.toggleButton.removeAttribute('data-listener-attached');
    }
  }

  /**
   * Initializes the truncation state by measuring content
   */
  #initTruncation() {
    const { textContainer } = this.refs;
    if (!textContainer) return;

    // Measure full height first
    textContainer.style.height = 'auto';
    const fullHeight = textContainer.scrollHeight;

    // Apply line-clamp to get collapsed height
    textContainer.style.height = '';
    textContainer.classList.add('text-truncate--collapsed');
    
    // Use requestAnimationFrame to ensure styles are applied
    requestAnimationFrame(() => {
      this.#collapsedHeight = textContainer.offsetHeight;

      // Only show toggle button if content is truncated
      if (fullHeight > this.#collapsedHeight + 1) { // +1 for rounding tolerance
        this.refs.toggleButton?.classList.remove('hidden');
        this.dataset.truncatable = 'true';
        
        // Initialize MUI button state
        if (this.#isMuiVariant) {
          this.#updateMuiButton();
        }
      } else {
        this.refs.toggleButton?.classList.add('hidden');
        this.dataset.truncatable = 'false';
      }
    });
  }

  /**
   * Handles expanding the content
   * @returns {Promise<{startHeight: number, endHeight: number}>}
   */
  async #expand() {
    const { textContainer } = this.refs;
    if (!textContainer) return { startHeight: 0, endHeight: 0 };
    
    const startHeight = textContainer.offsetHeight;

    // Remove collapsed class and reset display
    textContainer.classList.remove('text-truncate--collapsed');
    textContainer.style.display = 'block';
    textContainer.style.webkitLineClamp = 'unset';
    textContainer.style.webkitBoxOrient = 'unset';
    textContainer.style.maxHeight = 'none';
    
    // Force reflow and wait for browser to recalculate
    void textContainer.offsetHeight;
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    const endHeight = textContainer.scrollHeight;
    
    return {
      startHeight,
      endHeight,
    };
  }

  /**
   * Handles collapsing the content
   * @returns {{startHeight: number, endHeight: number}}
   */
  #collapse() {
    const { textContainer } = this.refs;
    if (!textContainer) return { startHeight: 0, endHeight: 0 };
    
    const startHeight = textContainer.offsetHeight;
    const endHeight = this.#collapsedHeight;

    return { startHeight, endHeight };
  }

  /**
   * Initializes a height transition
   * @param {number} startHeight
   * @param {number} endHeight
   */
  #animateHeight(startHeight, endHeight) {
    const { textContainer } = this.refs;
    if (!textContainer) return;

    // Cancel any existing animation
    this.#animation?.cancel();

    // Set initial height and overflow
    textContainer.style.height = `${startHeight}px`;
    textContainer.style.overflow = 'hidden';
    
    // Force reflow
    void textContainer.offsetHeight;

    // Start animation
    requestAnimationFrame(() => {
      this.#animation = textContainer.animate(
        {
          height: [`${startHeight}px`, `${endHeight}px`],
        },
        {
          duration: this.#animationSpeed,
          easing: this.#animationEasing,
          fill: 'forwards',
        }
      );

      this.#animation.onfinish = () => this.#onAnimationFinish();
    });
  }

  /**
   * Handles the animation finish event.
   */
  #onAnimationFinish() {
    const { textContainer } = this.refs;
    if (!textContainer) return;

    // After animation, if collapsed, add the collapsed class and reset styles
    if (!this.#expanded) {
      textContainer.classList.add('text-truncate--collapsed');
      textContainer.style.height = '';
      textContainer.style.overflow = 'hidden';
      textContainer.style.display = '';
      textContainer.style.webkitLineClamp = '';
      textContainer.style.webkitBoxOrient = '';
      textContainer.style.maxHeight = '';
    } else {
      // If expanded, set height to auto and remove overflow restriction
      textContainer.style.height = 'auto';
      textContainer.style.overflow = '';
    }
  }

  /**
   * Toggles the truncation state of the content.
   *
   * @param {Event} event - The click event
   */
  toggle = async (event) => {
    if (event) {
      event.preventDefault();
    }

    // Toggle state before animation
    this.#expanded = !this.#expanded;

    this.dataset.expanded = this.#expanded ? 'true' : 'false';
    this.refs.toggleButton?.setAttribute('aria-expanded', this.dataset.expanded);

    // Update MUI button styles if this is the MUI variant
    if (this.#isMuiVariant && this.refs.toggleButton) {
      this.#updateMuiButton();
    }
    
    const { startHeight, endHeight } = this.#expanded 
      ? await this.#expand() 
      : this.#collapse();

    this.#animateHeight(startHeight, endHeight);
  };

  /**
   * Applies standard structure (non-MUI) to the text truncate element
   */
  #applyStandardStructure() {
    const { textContainer, toggleButton } = this.refs;
    if (!textContainer || !toggleButton) return;

    // Remove MUI section wrapper if exists
    const muiSection = this.querySelector('.text-truncate__mui-section');
    if (muiSection && textContainer.parentNode === muiSection) {
      muiSection.parentNode.insertBefore(textContainer, muiSection);
      muiSection.remove();
    }

    // Remove MUI button wrapper if exists
    const buttonWrapper = this.querySelector('.text-truncate__mui-button-wrapper');
    if (buttonWrapper && toggleButton.parentNode === buttonWrapper) {
      buttonWrapper.parentNode.insertBefore(toggleButton, buttonWrapper);
      buttonWrapper.remove();
    }

    // Apply standard button classes
    toggleButton.className = 'text-truncate__button hidden';
    toggleButton.innerHTML = `
      <span class="text-truncate__button-text-collapsed">Mehr anzeigen</span>
      <span class="text-truncate__button-text-expanded">Weniger anzeigen</span>
    `;
  }

  /**
   * Updates the MUI button styles and text
   */
  #updateMuiButton() {
    const button = this.refs.toggleButton;
    if (!button) return;

    if (this.#expanded) {
      button.style.setProperty('--lineWidth', '100%');
      button.textContent = 'Weniger';
    } else {
      button.style.setProperty('--lineWidth', '0%');
      button.textContent = 'Mehr';
    }
  }
}

if (!customElements.get('text-truncate')) {
  customElements.define('text-truncate', TextTruncateComponent);
}